export type DeliveryOutcome =
  | 'planned'
  | 'delayed'
  | 'running'
  | 'retrying'
  | 'sent'
  | 'failed'
  | 'rejected'
  | 'skipped'
  | 'cancelled'
  | 'unknown';

export type DeliveryOutcomeGroup = 'done' | 'problem' | 'attention' | 'pending' | 'other';

export type DeliveryOutcomeCounts = Partial<Record<DeliveryOutcome, number>>;

export const DELIVERY_OUTCOMES: DeliveryOutcome[] = ['planned', 'delayed', 'running', 'retrying', 'sent', 'failed', 'rejected', 'skipped', 'cancelled', 'unknown'];

export const DELIVERY_OUTCOME_GROUPS: DeliveryOutcomeGroup[] = ['done', 'problem', 'attention', 'pending', 'other'];

const GROUP_BY_OUTCOME: Record<DeliveryOutcome, DeliveryOutcomeGroup> = {
  sent: 'done',
  failed: 'problem',
  rejected: 'problem',
  skipped: 'other',
  retrying: 'attention',
  delayed: 'attention',
  planned: 'pending',
  running: 'pending',
  cancelled: 'other',
  unknown: 'other',
};

export const outcomeGroup = (outcome: DeliveryOutcome): DeliveryOutcomeGroup => GROUP_BY_OUTCOME[outcome];

export const isProblemOutcome = (outcome: DeliveryOutcome): boolean =>
  GROUP_BY_OUTCOME[outcome] === 'problem' || GROUP_BY_OUTCOME[outcome] === 'attention';

export const sumCounts = (counts: DeliveryOutcomeCounts, outcomes: DeliveryOutcome[] = DELIVERY_OUTCOMES): number =>
  outcomes.reduce((total, outcome) => total + (counts[outcome] ?? 0), 0);

export const groupCounts = (counts: DeliveryOutcomeCounts): Record<DeliveryOutcomeGroup, number> => {
  const result: Record<DeliveryOutcomeGroup, number> = { done: 0, problem: 0, attention: 0, pending: 0, other: 0 };
  for (const outcome of DELIVERY_OUTCOMES) {
    result[GROUP_BY_OUTCOME[outcome]] += counts[outcome] ?? 0;
  }
  return result;
};

export const successRate = (counts: DeliveryOutcomeCounts): number | undefined => {
  const finished = sumCounts(counts, ['sent', 'failed', 'rejected']);
  if (finished === 0) return undefined;
  return Math.round(((counts.sent ?? 0) / finished) * 100);
};

export interface DeliveryWindow {
  from: Date;
  to: Date;
}

export const deliveryWindowAround = (now: Date, days: number): DeliveryWindow => {
  const span = days * 24 * 60 * 60 * 1000;
  return { from: new Date(now.getTime() - span), to: new Date(now.getTime() + span) };
};

export const dayBounds = (day: string): DeliveryWindow => {
  const [year, month, date] = day.split('-').map(Number);
  const from = new Date(year, month - 1, date);
  const to = new Date(year, month - 1, date + 1);
  return { from, to };
};

export const daysInWindow = (window: DeliveryWindow): string[] => {
  const days: string[] = [];
  const cursor = new Date(window.from.getFullYear(), window.from.getMonth(), window.from.getDate());
  while (cursor < window.to) {
    const month = String(cursor.getMonth() + 1).padStart(2, '0');
    const date = String(cursor.getDate()).padStart(2, '0');
    days.push(`${cursor.getFullYear()}-${month}-${date}`);
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
};

export interface DeliveryEvent {
  uuid: string;
  sendingScheduleUuid: string;
  plannedAt: string;
  outcome: DeliveryOutcome;
  attempt: number;
  maxAttempts: number;
  attemptedAt?: string;
  finalizedAt?: string;
  nextAttemptAt?: string;
  errorCode: string;
  lastError: string;
  advertUuid: string;
  advertTitle: string;
  channelUuid: string;
  channelLink: string;
  telegramAccountUuid: string;
  telegramAccountPhone: string;
  accountId: number;
  clientEmail: string;
}

export interface DeliveryAttempt {
  attempt: number;
  at: string;
  error: string;
  errorCode: string;
}

export interface DeliveryEventDetail {
  event: DeliveryEvent;
  attempts: DeliveryAttempt[];
}

export interface DeliveryEventsPage {
  events: DeliveryEvent[];
  total: number;
}

export interface DeliverySummary {
  past: DeliveryOutcomeCounts;
  future: DeliveryOutcomeCounts;
  topErrors: { code: string; count: number }[];
}

export interface BlockedDeliveryTarget {
  channelUuid: string;
  channelLink: string;
  telegramAccountUuid: string;
  telegramAccountPhone: string;
  errorCode: string;
  lastError: string;
  lastErrorAt?: string;
  upcomingDeliveries: number;
}

export interface FailingTelegramAccount {
  telegramAccountUuid: string;
  telegramAccountPhone: string;
  lastErrorCode: string;
  lastErrorAt?: string;
}

export interface DeliveryAlerts {
  delayed: number;
  blockedTargets: BlockedDeliveryTarget[];
  failingAccounts: FailingTelegramAccount[];
}

export type DeliveryMatrixGroupBy = 'account' | 'channel';

export interface DeliveryMatrixRow {
  key: string;
  label: string;
  total: number;
  problems: number;
  cells: { day: string; counts: DeliveryOutcomeCounts }[];
}

export interface DeliveryEventFilters {
  outcomes: DeliveryOutcome[];
  telegramAccountUuid?: string;
  channelUuid?: string;
  errorCode?: string;
  day?: string;
}
